import { useEffect, useState } from 'react';
import { X, Clock } from 'lucide-react';
import { hexToRgba } from '~/lib/contrast';
import { DEFAULT_SCHOOL_THEME_DARK_HEX } from '~/styles/brand';

interface WaitlistJoinDialogProps {
  open: boolean;
  teacherName: string;
  /** e.g. "3:15 PM" — omitted when the waitlist is for the whole schedule. */
  timeLabel?: string;
  schoolName: string;
  email: string;
  phone: string;
  /** Whether the school has SMS notifications turned on at all. */
  smsEnabled: boolean;
  overlayColor?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (opts: { sms: boolean }) => void;
}

/**
 * Confirmation step for joining a waitlist. Joining used to be a one-click
 * action with no way to capture SMS consent, so the server (which only texts
 * parents who explicitly opted in) never texted waitlist parents when a spot
 * opened. This dialog adds the same opt-in checkbox and consent language the
 * booking review page uses. Email is always on — it's how the waitlist works.
 */
export function WaitlistJoinDialog({
  open,
  teacherName,
  timeLabel,
  schoolName,
  email,
  phone,
  smsEnabled,
  overlayColor,
  busy = false,
  onCancel,
  onConfirm,
}: WaitlistJoinDialogProps) {
  const [smsOptIn, setSmsOptIn] = useState(false);
  const hasPhone = phone.replace(/\D/g, '').length >= 10;

  // Every opening starts unchecked: consent is per request, never remembered.
  useEffect(() => {
    if (open) setSmsOptIn(false);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in"
      style={{ backgroundColor: hexToRgba(overlayColor || DEFAULT_SCHOOL_THEME_DARK_HEX, 0.85) }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-dialog-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h2 id="waitlist-dialog-title" className="text-lg font-heading font-bold text-gray-900">Join the waitlist</h2>
            <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1.5">
              <Clock size={14} className="text-gray-400 flex-shrink-0" />
              <span>
                {teacherName}
                {timeLabel ? ` · ${timeLabel}` : ''}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700">
            We&apos;ll email <strong className="font-semibold">{email}</strong> if this time opens up.
          </p>

          {smsEnabled && (
            <div className="space-y-2">
              <label className={`flex items-center gap-2 ${hasPhone ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  disabled={!hasPhone}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Also text me if a spot opens</span>
              </label>
              {!hasPhone ? (
                <p className="text-xs text-gray-500 pl-6">Add a phone number on the first step to get a text.</p>
              ) : (
                <>
                  {/* Same consent language as the booking review page. */}
                  <p className="text-xs text-gray-600 leading-relaxed pl-6">
                    By checking this box, I agree to receive text messages from{' '}
                    <strong className="font-semibold">{schoolName || 'this school'}</strong> about this waitlist and my conference appointments.
                    Message frequency varies. Message and data rates may apply. Text <strong>HELP</strong> for help, <strong>STOP</strong> to cancel at any time.
                  </p>
                  <div className="text-xs text-gray-500 pl-6">
                    <a href="https://schoolconferencego.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
                    {' · '}
                    <a href="https://schoolconferencego.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ sms: smsEnabled && hasPhone && smsOptIn })}
            className="btn btn-primary"
            disabled={busy}
          >
            {busy ? 'Joining…' : 'Join waitlist'}
          </button>
        </div>
      </div>
    </div>
  );
}
